import React, {Component} from 'react'
import {Button, Icon, Modal, Spin, Tooltip} from "antd";
import Highlighter from 'react-highlight-words';
import CountUp from 'react-countup'
import DataManager from "../DataManagers/DataManager";
import {LinkWithIcon} from "./MapHoverTooltipLayer";
import {
	downloadAsCSV,
	fieldValueIsALink,
	getCountyFIPSCodeFromCensusBlockFIPSCode,
	isACensusTract,
} from "../utilsData";
import {ACS_DATA_NAME_ACCESSOR, ACS_DATA_NAME_ACCESSOR_IN_TRACT_DATA, STATE_NAME_ACCESSOR} from "../constants";
import {capitalize, humanReadableFields, smartDisplayValue,} from "./utilsDisplay";
import {FULL_STATE_NAME_TO_ABBREVIATION, HIGHLIGHT_UI_COLOR} from "../DataManagers/utilsColorTheme";
import {smartRound} from "../utils";
import {USASpendingDataModalAndButton} from "./USASpendingDataModalAndButton/USASpendingDataModalAndButton";


export const interpretStringAsHTML = formattedText => {
	if (typeof(formattedText) === 'string') {
		return (
			<span dangerouslySetInnerHTML={{__html: formattedText}} />
		)
	}
	return formattedText
}

export const CollapseButton = ({isCollapsed, onToggle, expandText = 'Expand', collapseText = 'Collapse', img=null}) => (
	<Tooltip trigger={['hover']} title={isCollapsed ? "Show Dashboard" : "Hide Dashboard"} placement="bottom" overlayClassName="close-dashboard-tooltip">
		<Button onClick={onToggle}>
			<img src={isCollapsed ? "cori-icons/down.png" : "cori-icons/up.png"} alt="Close"/>
			{isCollapsed ? expandText : collapseText}
		</Button>
	</Tooltip>
)

export const SpinIcon = ({large = false}) => <Icon type="loading" style={{ fontSize: large? 48 : 24 }} spin />

const LARGE_WIDTH = 300
const SMALL_WIDTH = 12
export const VizNoDataStateMessage = ({large = false}) => (
	<div style={{minWidth: large ? LARGE_WIDTH : SMALL_WIDTH, marginLeft: large ? -LARGE_WIDTH / 2 : -SMALL_WIDTH / 2, marginTop: -20}}>
		<SpinIcon large={large}/>
		<div style={{fontSize: large ? 24 : 12}}>
			Hang tight! We're pulling up the data...
		</div>
	</div>
)

export const PanelDescriptionText = ({text, style={}}) => {
	return (
		<div className="tiny-italic-gray-text" style={style}>
			{text}
		</div>
	)
}

export const DataLoadingComponentContainer = ({dataIsLoading, children}) => (
	<Spin spinning={dataIsLoading} indicator={VizNoDataStateMessage({large: true})}>
		{children}
	</Spin>
)

// RURAL DEFINITION HELPERS

export class RuralDefinitionAndIcon extends Component {
	state = { visible: false }

	showModal = () => {
		this.setState({
			visible: true,
		})
	}

	handleCancel = () => {
		this.setState({
			visible: false
		})
	}

	render() {
		const {cartoLayerId} = this.props
		const {
			nameLong,
			organizationLong,
			organizationShort,
			summary,
			detailedExplanation,
			moreInfoURL
		} = DataManager.stats.getRuralDefinitionInfo(cartoLayerId)

		return (
			<div>
				<Tooltip
					placement="right"
					title={
						<div>
							<div>{summary}</div>
							{(detailedExplanation || moreInfoURL) && <Button className="more-info-button" onClick={this.showModal}>
								{'More information'}
							</Button>}
						</div>
					}
				>
					<Icon type="info-circle" style={{ padding: 4 }} />
				</Tooltip>
				{`${organizationShort} - ${nameLong}`}

				{/*NOTE: this modal is only visible when state.visible === true*/}
				<Modal
					title={
						<>
							<div className="big-bold-header-text">{nameLong}</div>
							<div className="tiny-italic-gray-text">{organizationLong}</div>
						</>
					}
					visible={this.state.visible}
					// onOk={this.handleCancel}
					onCancel={this.handleCancel}
					footer={null}
				>
					{detailedExplanation.split('\n')
						.filter(line => !!line)
						.map((line, i) => (<p key={i}>{line}</p>))
					}
					{<LinkWithIcon href={moreInfoURL}>
						More information
					</LinkWithIcon>}
				</Modal>
			</div>
		)
	}
}

export const DownloadDataButton = ({data, buttonText = 'Download as csv', fileName}) => {
	return (
		<div className='download-button'>
		<Button onClick={() => downloadAsCSV(data, fileName)}>
			<img src="cori-icons/download.png" alt="Download icon"/>
			{buttonText}
		</Button>
		</div>
	)
}

export const EditButton = ({onClick, buttonIsActive = undefined, showJustIcon = false, tooltipText = 'Filter based on this indicator by manually adjusting filter bounds'}) => {
	return (
		<Tooltip className="community-summary-edit-button" placement="topRight" title={tooltipText} mouseEnterDelay={0.8}>
			<Button type={getButtonTypeFromBool(buttonIsActive)} onClick={onClick}>
				<Icon type="edit" />
				{!showJustIcon && <span style={{lineHeight: '2.5em'}}>Edit</span>}
			</Button>
		</Tooltip>
	)
}

export const CORILogo = () => <img className="cori-logo" src="cori-logo-mark-black.png" alt="Center on Rural Innovation logo" />

export const getButtonTypeFromBool = boolValue => boolValue ? 'primary' : 'dashed'

// Community Summary Menu

// Reason for memoization: antd Tooltips are (surprisingly) very expensive
const toggleButtonPropsAreEqual = (prevProps, nextProps) => {
	const {buttonText, tooltipText, buttonIsActive} = prevProps
	return (buttonText === nextProps.buttonText)
	&& (tooltipText === nextProps.tooltipText)
	&& (buttonIsActive === nextProps.buttonIsActive)
}

export const ToggleButton = React.memo(
	({buttonText, tooltipText, buttonIsActive = false, onClick}) => (
		<Tooltip placement="topRight" title={tooltipText} mouseEnterDelay={0.8}>
			<Button type={getButtonTypeFromBool(buttonIsActive)} onClick={onClick}>
				{buttonText}
			</Button>
		</Tooltip>
	),
	toggleButtonPropsAreEqual
)

export const StatSectionHeaderRow = React.memo(
	({sectionName = '', comparisonCommunityExists, showOnlyOnHover = false, stateName = false}) => (
		<div className={`row summary-stat-row section-header ${!sectionName ? 'just-column-names' : ''}`}>
			<div className='summary-stat-name summary-title'>{sectionName.toUpperCase()}</div>
			<div className={`summary-stat-value summary-title first-value selected-region ${showOnlyOnHover && 'show-only-on-hover'}`}>LOCAL</div>
			{comparisonCommunityExists && <div className={`summary-stat-value summary-title comparison-region ${showOnlyOnHover && 'show-only-on-hover'}`}>COMPARE</div>}
			<div className={`summary-stat-value summary-title ${showOnlyOnHover && 'show-only-on-hover'}`}>{stateName || 'STATE'}</div>
			<div className={`summary-stat-value summary-title  ${showOnlyOnHover && 'show-only-on-hover'}`}>USA</div>
			<div className={`summary-stat-value summary-title summary-filter-button-col  ${showOnlyOnHover && 'show-only-on-hover'}`}>FILTER FOR..</div>
		</div>
	)
)

export const TableCell = text => (
	<div
		className="table-cell-content"
		style={{ wordWrap: "break-word", wordBreak: "break-word" }}
	>
		{fieldValueIsALink(text) ? <LinkWithIcon href={text}/> : text}
	</div>
)

export const TableCellWithHighlighting = (text, textToHighlight) => {
	if (!isNaN(text)) {
		return TableCell(smartDisplayValue(Number(text)))
	}
	const potentiallyHighlightedText = fieldValueIsALink(text) ? text : (<Highlighter
		highlightStyle={{ backgroundColor: HIGHLIGHT_UI_COLOR, padding: 0 }}
		searchWords={[textToHighlight]}
		autoEscape
		textToHighlight={`${text}`}
	/>)
	return TableCell(potentiallyHighlightedText)
}

export const TableColumnTitleCell = ({fieldName, cartoLayerId = undefined}) => TableCell(humanReadableFields(fieldName, cartoLayerId))

export const LocationTitleForCommunitySummary = (selectedGeography, selectedRegionACSDataRow, showLinkIcon = true) => {
	const stateName = selectedRegionACSDataRow[STATE_NAME_ACCESSOR]
	if (!stateName) return ''

	const _isACensusTract = isACensusTract(selectedGeography.cartoLayerId)
	const regionName = _isACensusTract ? selectedRegionACSDataRow[ACS_DATA_NAME_ACCESSOR_IN_TRACT_DATA] : selectedRegionACSDataRow[ACS_DATA_NAME_ACCESSOR]
	const stateAbbreviation = FULL_STATE_NAME_TO_ABBREVIATION[stateName]
	const locationText = _isACensusTract ? `OZ in ${regionName}, ${stateAbbreviation}` : `${regionName}, ${stateAbbreviation}`
	const wikipediaURL = _isACensusTract ? getWikipediaURL(regionName, stateName) : selectedRegionACSDataRow.wikipedia
	return (
		<div>
			<LinkWithIcon href={wikipediaURL} showLinkIcon={showLinkIcon}>
				{locationText}
			</LinkWithIcon>
			{_isACensusTract && <div className = 'summary-tract-label'>{selectedRegionACSDataRow[ACS_DATA_NAME_ACCESSOR]}</div>}
		</div>
	)
}

// EG: https://en.wikipedia.org/wiki/Madison_County,_Alabama
const getWikipediaURL = (regionName, stateName) => `https://en.wikipedia.org/wiki/${regionName.replace(' CDP', '').replace(' ', '_')},_${stateName}`

export const RegionName = React.memo(
	({selectedGeography, selectedRegionACSDataRow, isComparison, geoIdForSelectedRegion}) => {
		const regionText = LocationTitleForCommunitySummary(selectedGeography, selectedRegionACSDataRow)
		
		if (!regionText) return null
		const regionCSSClass = isComparison ? 'comparison-region' : 'selected-region'
		return (
			<div className={regionCSSClass}>
				<div className={`tiny-italic-gray-text ${regionCSSClass}`}>{isComparison ? 'Comparison (clicked)' : 'Selected Community'}</div>
				<div className ='state'>
					{regionText}
				</div>
				<USASpendingDataModalAndButton
					countyFIPSCode={getCountyFIPSCodeFromCensusBlockFIPSCode(geoIdForSelectedRegion)}
					selectedGeography={selectedGeography}
				/>
			</div>
		)
	},
)

export const FilterIcon = ({searchIsActive = false}) => (<Icon type="search" style={{ color: searchIsActive ? '#1890ff' : undefined }} />)

export const FilterStatusCount = ({activeData, totalData, useRegularSizedNumber = false}) => (
	<span>
		<span style={{fontWeight: 800, fontSize: useRegularSizedNumber ? 14 : 32, color: HIGHLIGHT_UI_COLOR}}>
			<CountUp end={activeData.length} preserveValue/>
		</span>
		{` / `}
		<CountUp end={totalData.length} preserveValue/>
		{` (`}<CountUp end={smartRound(100 * (activeData.length / totalData.length))} preserveValue decimals={1}/>{`%)`}
	</span>
)

export const GeographyOption = ({geographyName}) => (
	<div className='dropdown-default'>
		<div className='dropdown-bold'>{capitalize(geographyName)}</div>
	</div>
)
